import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomCascadeSelect } from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { createPayment, createPropertyPayment } from "@/store/paymentSlice";
import { fetchUnitsForRentRoll } from "@/store/unitSlice";
import ipponApi from "@/config/axios";
import { AREA_TYPE_LABELS } from "@ippon/shared-types";
import {
  fetchBudgetCategories,
  createBudgetCategory,
} from "@/store/budgetSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import PaymentDrawer, {
  type PaymentDrawerFormValues,
  type SectionPaymentItemOption,
} from "./PaymentDrawer";

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  /**
   * 収益区分 (revenue_group) の初期値。指定時はカテゴリを初期選択して開く。
   * 収入タブ: 選択中 CardTab。予実表「その他」導線: "other"。
   */
  initialCategory?: string;
  /** 登録成功後に呼ばれる (一覧 / 予実の再取得など、再取得対象は親が決める)。 */
  onCreated?: () => void;
}

/**
 * 収入登録モーダル (段階式: カテゴリ → 区画 → 科目。支出と同じ流れ)。
 *
 * - 貸室 (stock_rent) / その他収入 (stock_other): 区画紐付け入金 (unit_id 付き)
 * - その他 (other): Unit 非紐付け入金 (契約に紐づかない収入も吸収)
 *
 * 収入サブタブ (IncomeListTab) と予実表「その他」配下の「収入を追加」で共用する。
 */
const IncomeCreateDrawer: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  initialCategory,
  onCreated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const budgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );
  useEffect(() => {
    dispatch(fetchBudgetCategories() as any);
  }, [dispatch]);

  // 選択区画の現契約者 (テナント) 表示用: 物件配下の active 契約を unit_id → contractor_name で引く。
  // (IncomeListTab と同機構。記録される契約者は請求年月時点で BE が確定 = 案C-b。ここは入力補助の表示。)
  const [tenantByUnitId, setTenantByUnitId] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    if (!open || !propertyId) return;
    let cancelled = false;
    ipponApi
      .get(`${import.meta.env.VITE_API_URL}/api/v1/contracts`, {
        params: { property_id: propertyId, status: "active" },
      })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.data?.items || []) as any[];
        const byUnit: Record<string, any> = {};
        for (const c of items) {
          const uid = c?.unit_id;
          if (!uid) continue;
          const prev = byUnit[uid];
          if (
            !prev ||
            new Date(c.created_at ?? 0).getTime() >
              new Date(prev.created_at ?? 0).getTime()
          ) {
            byUnit[uid] = c;
          }
        }
        const map: Record<string, string> = {};
        Object.keys(byUnit).forEach((uid) => {
          map[uid] = String(byUnit[uid]?.contractor_name || "");
        });
        setTenantByUnitId(map);
      })
      .catch(() => setTenantByUnitId({}));
    return () => {
      cancelled = true;
    };
  }, [open, propertyId]);

  const [formCategory, setFormCategory] = useState<string>(""); // revenue_group
  const [formAreaType, setFormAreaType] = useState<string>(""); // area_type (場所区分)
  const [formUnitId, setFormUnitId] = useState<string>("");

  const [units, setUnits] = useState<any[]>([]);

  // 物件配下の収益区画 (貸室=stock_rent / その他収入=stock_other) を取得
  useEffect(() => {
    if (!propertyId) return;
    dispatch(fetchUnitsForRentRoll({ property_id: propertyId }) as any)
      .unwrap()
      .then((list: any[]) => setUnits(Array.isArray(list) ? list : []))
      .catch(() => setUnits([]));
  }, [dispatch, propertyId]);

  // モーダルを開くたびにフォーム選択をリセット。
  // 収益区分 (カテゴリ) は呼び出し元で既に決まっていれば初期値に入れる。
  useEffect(() => {
    if (open) {
      setFormCategory(initialCategory ?? "");
      setFormAreaType("");
      setFormUnitId("");
    }
  }, [open, initialCategory]);

  // 区画(unit)を選んでいれば区画紐付け、未選択なら property レベル(非紐付け)収入。
  const isUnitLinked = Boolean(formUnitId);

  // 科目選択肢: 収入 BudgetCategory を全て (収益カテゴリで絞らない=明細フリー)。
  // 各科目の revenue_group をラベルにして「{明細項目} {収益カテゴリ}」表示に使う。
  // 固定3つはラベル変換、カスタム収益カテゴリは値(=名称)をそのまま。
  const rgLabel = (rg: string): string =>
    rg === "stock_rent"
      ? "貸室"
      : rg === "stock_other"
        ? "付帯"
        : rg === "other"
          ? "その他"
          : rg;
  const formPaymentItems: SectionPaymentItemOption[] = useMemo(() => {
    return (budgetCategories as any[])
      .filter((c) => c && c.category_type === "income" && !c.deleted_at)
      .map((c) => ({
        category_id: c.category_id,
        name: c.name,
        defaultAmount: 0,
        categoryLabel: rgLabel(String(c.revenue_group || "other")),
      }));
  }, [budgetCategories]);

  // 収益カテゴリ選択肢 (新規登録モーダル用): 固定3つ + 既存カスタムを distinct で。
  const revenueGroupOptions = useMemo(() => {
    const base = [
      { value: "stock_rent", label: "貸室" },
      { value: "stock_other", label: "付帯" },
      { value: "other", label: "その他" },
    ];
    const seen = new Set(base.map((b) => b.value));
    for (const c of budgetCategories as any[]) {
      const rg = c?.category_type === "income" ? c.revenue_group : undefined;
      if (rg && !seen.has(rg)) {
        seen.add(rg);
        base.push({ value: rg, label: rg });
      }
    }
    return base;
  }, [budgetCategories]);

  // セレクトでの科目ローカル追加 → 保存時に選択中カテゴリの revenue_group で永続化
  const persistNewCategory = useCallback(
    async (name: string): Promise<string> => {
      const created: any = await dispatch(
        createBudgetCategory({
          name: name.trim(),
          category_type: "income",
          revenue_group: (formCategory || "other") as
            | "stock_rent"
            | "stock_other"
            | "other",
        }) as any,
      ).unwrap();
      return created.category_id as string;
    },
    [dispatch, formCategory],
  );

  // 明細セレクト「新規登録」モーダル: 収益カテゴリ + 明細項目 を即時作成 → 一覧再取得 → category_id を返す。
  const handleAddCategory = useCallback(
    async (
      name: string,
      revenueGroup: string,
    ): Promise<string | undefined> => {
      const created: any = await dispatch(
        createBudgetCategory({
          name: name.trim(),
          category_type: "income",
          revenue_group: revenueGroup as "stock_rent" | "stock_other" | "other",
        }) as any,
      ).unwrap();
      await dispatch(fetchBudgetCategories() as any);
      return created?.category_id as string | undefined;
    },
    [dispatch],
  );

  // カスケード = 区画への任意リンク専用。場所区分の選択肢 = 区画の area_type (distinct)。
  // 「その他(区画なし)」は廃止 — 区画未選択で property レベル収入、収益カテゴリは明細から決まる。
  const areaOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of units as any[]) {
      if (!u || u.deleted_at) continue;
      const at = String(u.area_type || "");
      if (at && !seen.has(at)) {
        seen.set(at, (AREA_TYPE_LABELS as any)[at] || at);
      }
    }
    return Array.from(seen, ([v, label]) => ({ value: v, label }));
  }, [units]);

  // カスケード行 = 区画 (場所区分→名称→テナント)。収益カテゴリは明細＋区画から確定するので列に持たない。
  const cascadeRows = useMemo(
    () =>
      (units as any[])
        .filter((u) => u && !u.deleted_at)
        .map((u) => ({
          area: u.area_type as string,
          area_label: (AREA_TYPE_LABELS as any)[u.area_type] || u.area_type,
          name: u.unit_id as string,
          name_label: u.landmark_name || u.unit_id,
          tenant: tenantByUnitId[u.unit_id] || null,
        })),
    [units, tenantByUnitId],
  );

  // フォーム先頭に差し込むカスケード (PaymentDrawer の leadingFields)
  const leadingFields = (
    <CustomCascadeSelect
      boxed
      independentModeDrafts
      title="場所・テナント選択"
      columns={[
        {
          key: "area",
          label: "場所区分",
          role: "select",
          placeholder: "場所区分を選択",
          options: areaOptions,
        },
        {
          key: "name",
          label: "名称",
          role: "select",
          placeholder: "名称を選択",
        },
        {
          key: "tenant",
          label: "テナント名",
          role: "auto",
          hint: "名称を選択すると自動で表示されます",
          emptyText: "テナント登録なし",
        },
      ]}
      rows={cascadeRows}
      value={{
        area: formAreaType,
        name: formUnitId,
      }}
      onChange={(n) => {
        // カスケード = 区画への任意リンク専用。収益カテゴリは明細(科目)から決まるので持たない。
        setFormAreaType(n.area || "");
        setFormUnitId(n.name || "");
      }}
      reverse={{
        key: "tenant",
        forwardLabel: "場所から探す",
        reverseLabel: "テナントから探す",
        searchPlaceholder: "テナント名で検索...",
        emptyText: "該当するテナントがありません",
      }}
      labelWidth="130px"
      inputWidth="100%"
    />
  );

  const handleCreateIncome = useCallback(
    async (values: PaymentDrawerFormValues) => {
      // 区画(カスケード)は任意リンク。未選択でも property レベル収入として登録可。
      // 収益カテゴリは明細(科目)から決まるため、区画/カテゴリの必須検証は無し。
      try {
        // ローカル新規追加された科目 (isNew) を先に永続化して category_id を確定
        const items = await Promise.all(
          values.items.map(async (i) => {
            let categoryId = i.category_id;
            if ((i.isNew || !categoryId) && i.name.trim()) {
              categoryId = await persistNewCategory(i.name);
            }
            const amount = i.amount ? parseFloat(i.amount) : 0;
            const taxRate = i.tax_category === "taxable" ? 10 : 0;
            return {
              category_id: categoryId,
              name: i.name,
              amount,
              tax_category: i.tax_category,
              tax_rate: taxRate,
              tax_amount: Math.floor((amount * taxRate) / 100),
            };
          }),
        );

        const common = {
          property_id: propertyId,
          status: values.status,
          items,
          title: values.title?.trim() || undefined,
          aggregation_month: values.aggregation_month,
          scheduled_date: values.scheduled_date || undefined,
          transfer_datetime: values.transfer_datetime || undefined,
          transfer_method: values.transfer_method || undefined,
          remarks: values.remarks || undefined,
        };

        if (isUnitLinked) {
          // 貸室 / その他収入: 区画紐付け入金 (unit_id 付き)
          await dispatch(
            createPayment({
              propertySectionId: formUnitId,
              ...common,
            }) as any,
          ).unwrap();
        } else {
          // その他: Unit 非紐付け入金
          await dispatch(createPropertyPayment(common) as any).unwrap();
        }
        showToast({ type: "success", message: "収入を登録しました" });
        onCreated?.();
      } catch (e: any) {
        if (
          e?.message === "category required" ||
          e?.message === "area required" ||
          e?.message === "unit required"
        ) {
          throw e;
        }
        showToast({ type: "error", message: "登録に失敗しました" });
        throw new Error("create failed");
      }
    },
    [
      dispatch,
      propertyId,
      isUnitLinked,
      formCategory,
      formAreaType,
      formUnitId,
      persistNewCategory,
      showToast,
      onCreated,
    ],
  );

  return (
    <PaymentDrawer
      open={open}
      onClose={onClose}
      onSubmit={handleCreateIncome}
      title="収入登録"
      leadingFields={leadingFields}
      sectionPaymentItems={formPaymentItems}
      onAddPaymentCategory={handleAddCategory}
      revenueGroupOptions={revenueGroupOptions}
      allowAddPaymentItem
      isEdit={false}
    />
  );
};

export default IncomeCreateDrawer;
